import { publicNames as corePublicNames } from '../../../../services/EpisodeScriptService';
import type { PublicNameDomain, PublicNames } from '../../../../services/EpisodeScriptService';
import type { NamesTable } from '../../../../dorian-shared/types/episodeScript';

export type { PublicNameDomain, PublicNames };

/** The word Basil uses for a variable the book has no name for. */
export const UNNAMED_VARIABLE = 'unnamed variable';

/** What the surface calls a thing it cannot name (spec section 9.4). */
export const HUMAN_LABEL: Readonly<Record<'characters' | 'expressions' | 'locations', string>> = Object.freeze({
  characters: 'a character',
  expressions: 'a mood',
  locations: 'a location',
});

/** The kind of thing the margin question asks about. */
export const DOMAIN_WORDS: Readonly<Record<'characters' | 'expressions' | 'locations', string>> = Object.freeze({
  characters: 'character',
  expressions: 'mood',
  locations: 'location',
});

/**
 * What the margin says when a name cannot be shown (Basil v0.2, P2).
 *
 * The old note asked the writer a question — "which character is it?" — that they had no way to
 * answer and no reason to be asked. These say the same fact three ways, one per thing, and each one
 * does three jobs: it REASSURES (the line is unharmed), it INSTRUCTS (leave the label alone), and it
 * offers an ACTION that exists (the protected exit into the scene).
 *
 * A writer is never taught to edit an identifier. The label stays exactly as it is; the scene's own
 * editor is where the thing behind it is looked at.
 */
export const MISSING_NAME_NOTE: Readonly<Record<'characters' | 'expressions' | 'locations', string>> = Object.freeze({
  characters: "This character's name can't be shown here; the line still belongs to them. "
    + 'Keep this speaker label unchanged. Open this scene to check the character.',
  expressions: "This mood's name can't be shown here; the line still has its mood. "
    + 'Keep this mood label unchanged. Open this scene to check the mood.',
  locations: "This location's name can't be shown here; the scene still takes place there. "
    + 'Keep this location label unchanged. Open this scene to check the location.',
});

/**
 * One name the script could not spell, and where it was found (Basil v0.2, P2; T17-06).
 *
 * The note matches the THING — character, mood or location — because "a name is missing" tells a
 * writer nothing about what they are looking at. The line and the scene travel with it, which is
 * what lets the surface put it beside the label it is about and offer the protected exit into that
 * scene rather than leaving the writer to find it themselves.
 */
export type NameGap = {
  domain: 'characters' | 'expressions' | 'locations';
  /** the whole note, exactly as the writer reads it. */
  note: string;
  /** the 1-based script line it was found on. */
  line: number;
  /** the node that line belongs to, for the protected exit. */
  branchId: string | null;
  /** the scene as the script spells it. */
  sceneName: string | null;
};

/** The heading each kind of missing name leads with. */
export const NAME_GAP_HEADLINE: Readonly<Record<NameGap['domain'], string>> = Object.freeze({
  characters: "A character's name can't be shown",
  expressions: "A mood's name can't be shown",
  locations: "A location's name can't be shown",
});

/**
 * T18-04: the one-line marker that stands beside the label itself.
 *
 * The margin is a strip of rows that must line up, row for row, with the script beside it: its
 * boxes are positioned against the script's own row heights, so a box taller than its row reaches
 * down over the rows below it. A whole paragraph in there overlapped the next label's guidance.
 *
 * The marker says WHICH kind of name cannot be shown and nothing else, in one line that fits the
 * row it belongs to. The sentence it summarises is in the guidance panel, where it has room of its
 * own and cannot cover anything.
 */
export const NAME_GAP_MARKER: Readonly<Record<NameGap['domain'], string>> = Object.freeze({
  characters: 'Character name not shown',
  expressions: 'Mood name not shown',
  locations: 'Location name not shown',
});

const ID_SPELLING = /^([CEL])(-?\d+)$/i;

const EMPTY_NAMES: PublicNames = Object.freeze({
  characters: {},
  expressions: {},
  locations: {},
  variables: {},
}) as PublicNames;

/**
 * The core's own flattener (EpisodeScriptService/viewModel/publicNames, the port of the loopback
 * server's `publicNames`, serve.mjs:53-55), with the null the surface can legitimately hold: the
 * names table does not exist until the episode has been read.
 *
 * Amendment A5: the flattening is not re-implemented here, only guarded.
 */
export function publicNames(table: NamesTable | null | undefined): PublicNames {
  if (!table) {
    return EMPTY_NAMES;
  }

  return corePublicNames(table);
}

/** What `humanise` found: the words to print, and the margin question to ask when there are none. */
export type HumanisedName = {
  text: string;
  question: string | null;
};

/**
 * Ids never reach a writer. `C12` reads as the book's word for that character, or as "a character"
 * with a question for the margin — never as the engine's spelling (editor.js:223-233).
 */
export function humanise(
  names: PublicNames,
  domain: 'characters' | 'expressions' | 'locations',
  value: unknown,
): HumanisedName {
  const text = String(value ?? '');
  const spelled = text.match(ID_SPELLING);
  if (!spelled) {
    return { text, question: null };
  }
  const named = names[domain]?.[spelled[2]];
  // Basil v0.2, P2: a names table always HAS an entry for every id in the episode — the decompiler
  // seeds `byId` with the id's own spelling and the book's words overwrite the ones they cover
  // (`decompile.ts:65-69`). So "there is an entry" was never the question; the question is whether
  // the entry is a WORD or the id wearing a hat. `variableWord` has always asked it that way, and
  // asking it here is what turns the margin note back on for a character the book cannot name.
  if (named && named.toLocaleUpperCase() !== `${spelled[1].toLocaleUpperCase()}${spelled[2]}`) {
    return { text: named, question: null };
  }

  return {
    text: HUMAN_LABEL[domain],
    question: MISSING_NAME_NOTE[domain],
  };
}

/** What `variableWord` found: the word to print, the id it kept out of the words, and whether it is known. */
export type VariableWord = {
  word: string;
  id: string | null;
  known: boolean;
};

/**
 * A variable is a word or it is nothing (editor.js:207-217). An unnamed one reads as
 * "unnamed variable"; its id is counted into the one collapsed margin note and appears nowhere else.
 */
export function variableWord(names: PublicNames, token: unknown): VariableWord {
  const matched = String(token ?? '').match(/^V(-?\d+)$/i);
  if (!matched) {
    return { word: String(token ?? ''), id: null, known: true };
  }
  const id = matched[1];
  const named = names.variables?.[id];
  if (named && named !== `V${id}`) {
    return { word: named, id, known: true };
  }

  return { word: UNNAMED_VARIABLE, id, known: false };
}
